import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { BaseState, withCrudOperations } from '../crud.state';
import { Indicator } from '../../models/indicator';
import { withIndicatorSelectors } from './indicator.selectors';
import { IndicatorService } from '../../services/indicator/indicator.service';
import { withEventBroadcasting } from '../features/event-broadcasting.feature';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { SocketService } from '../../services/socket.service';
import { downloadBlob } from '../../utility/download';
import { withLogging } from '../features/logger.feature';
import { ActiveUserStore } from '../user/active-user.state';

export type IndicatorState = BaseState<Indicator> & {
  selectedChild?: any;
  pool?: any;
  closeChild?: boolean;
};

export const initialState: IndicatorState = {
  items: {},
  loading: false,
  selectedId: '',
  selection: [],
  selectedChild: undefined,
  initialized: false,
  itemsLoaded: false,
  version: 0,
  lastPersistedId: '',
  trigger: undefined,
  entityLabel: 'Indikator',
  entityType: 'Indicator',
  pool: [],
  filter: {
    query: '',
    category: [],
    tags: undefined,
    products: undefined,
    published: null,
    locked: null,
    ids: undefined,
  },
  events: {
    add: ['rebuildTree'],
    update: ['rebuildTree'],
    delete: ['rebuildTree', 'removeChildren'],
    socket: ['rebuildTree'],
  },
};

export const IndicatorStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withCrudOperations<Indicator>(IndicatorService),
  withLogging(initialState.entityType),
  withIndicatorSelectors(),
  withEventBroadcasting(),
  withMethods(
    (
      store,
      indicatorService = inject(IndicatorService),
      socketService = inject(SocketService)
    ) => ({
      filterByCategory(categoryId: string) {
        return Object.values(store.items()).filter(item => {
          return item.category === categoryId;
        }) as Indicator[];
      },
      filterPoolByParent(parentId: string) {
        if (!store.pool) return [];
        return store
          .pool()
          .filter((item: Indicator) => {
            return item.parent === parentId;
          })
          .sort(
            (a: Indicator, b: Indicator) => a.sorting - b.sorting
          ) as Indicator[];
      },
      updateLockStatus: rxMethod<{ ids: Array<string>; isLocked: boolean }>(
        pipe(
          switchMap(({ ids, isLocked }) => {
            return indicatorService.updateLockStatus(ids, isLocked).pipe(
              tapResponse({
                next: response => {
                  const updatedItems = { ...store.items() };
                  ids.forEach(id => {
                    updatedItems[id].locked = isLocked;
                  });

                  patchState(store, {
                    items: updatedItems,
                    trigger: store.events ? store.events().update : undefined,
                  });
                  setTimeout(
                    () => patchState(store, { trigger: undefined }),
                    0
                  );

                  // emit socket event after update item
                  socketService.emitEvent('baseEvent', {
                    action: 'persistMultiple',
                    commune: 'dev',
                    body: updatedItems,
                    type: 'indicator',
                    trigger: store.events ? store.events().socket : undefined,
                  });
                },
                error: console.error,
                //finalize: () => patchState(store, { loading: false }),
              })
            );
          })
        )
      ),
      convertToXlsx: rxMethod<{ indicatorRows: Array<string[]> }>(
        pipe(
          switchMap(({ indicatorRows }) => {
            return indicatorService.convertToXlsx(indicatorRows).pipe(
              tapResponse({
                next: blob => {
                  patchState(store, {
                    selection: [],
                  });
                  downloadBlob(blob);
                },
                error: console.error,
                //finalize: () => patchState(store, { loading: false }),
              })
            );
          })
        )
      ),

      /**
       * fill pool state with pool-indicators
       */
      async findPool() {
        patchState(store, { loading: true });

        const items = await indicatorService.getPoolAsPromise();
        patchState(store, {
          pool: items,
          loading: false,
          initialized: true,
          itemsLoaded: true,
        });
      },

      /**
       * find by category-ids
       */
      async findByCategoryIds(categoryIds: string[]) {
        patchState(store, { loading: true });

        const items = await indicatorService.getByCategoryIds(categoryIds);
        patchState(store, {
          items: items,
          loading: false,
          initialized: true,
          itemsLoaded: true,
        });
      },

      /**
       * deselect indicator-child
       */
      deselectChild(): void {
        patchState(store, () => ({
          selectedChild: undefined,
          closeChild: false,
        }));
      },

      /**
       * select indicator-child
       * @param child
       */
      selectChild(child: any): void {
        patchState(store, () => ({
          selectedChild: child,
          closeChild: false,
        }));
      },

      /**
       * select indicator-child
       * @param child
       */
      updateChildAndClose(child: any): void {
        patchState(store, () => ({
          selectedChild: child,
          closeChild: true,
        }));
      },

      /**
       * select indicator-child
       */
      closeChildOverlay(): void {
        patchState(store, () => ({
          selectedChild: undefined,
          closeChild: true,
        }));
      },
    })
  ),
  withComputed(({ selectedChild, items }) => ({
    selectedChild: computed(() => {
      if (selectedChild && selectedChild()) {
        const childId = selectedChild()._id;
        return childId
          ? { ...items()[childId], ...selectedChild() }
          : selectedChild();
      } else return undefined;
    }),
  })),
  withHooks({
    onInit(store, activeUserStore = inject(ActiveUserStore)) {
      if (
        activeUserStore.canSeeAllInWorkspaceType('indicatorCategory') &&
        activeUserStore.context() !== 'atlasExternal'
      ) {
        store.loadAllItemsByPromise();
      }
    },
    onDestroy() {
      console.log('on destroy');
    },
  })
);
